import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { notificationService } from "../../services/notificationService";

export interface NotificationItem {
  id: string;
  type: string;
  message: string;
  metadata?: Record<string, any> | null;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

interface NotificationState {
  items: NotificationItem[];
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  items: [],
  loading: false,
  error: null,
};

// ---- Thunks ----

export const fetchUserNotifications = createAsyncThunk<NotificationItem[]>(
  "notifications/fetchUser",
  async (_, { rejectWithValue }) => {
    try {
      const res = await notificationService.getUserNotifications();
      return res.data ?? [];
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch notifications");
    }
  }
);

export const fetchAdminNotifications = createAsyncThunk<NotificationItem[]>(
  "notifications/fetchAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const res = await notificationService.getAdminNotifications();
      return res.data ?? [];
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch admin notifications");
    }
  }
);

// ---- Slice ----

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Ajout temps réel via WebSocket
    addNotification: (state, action: PayloadAction<NotificationItem>) => {
      // Éviter les doublons
      const exists = state.items.some((n) => n.id === action.payload.id);
      if (!exists) {
        state.items.unshift(action.payload);
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notif = state.items.find((n) => n.id === action.payload);
      if (notif) notif.isRead = true;
    },
    markAllAsRead: (state) => {
      state.items.forEach((n) => { n.isRead = true; });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.items = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAdminNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAdminNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addNotification,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
